import { useLazyQuery } from '@apollo/react-hooks'
import Spacer from '@berlitz/spacer'
import { ICertData } from '@interfaces/Achievements'
import { StoreProps } from '@interfaces/StoreState'
import { getLocale } from '@layouts/PortalTranslations/utils'
import { CertPanel, CertShareLabel, CertShareLink, CertWrapper } from '@layouts/Settings/Layout/style'
import { GET_TRANSLATIONS } from '@queries/translations'
import { CertificateTypes, PROFILE_TYPE, PROGRAM_TYPES } from '@utils/constants'
import * as React from 'react'
import { useEffect, useState } from 'react'
import { IntlProvider, useIntl } from 'react-intl'
import { useSelector } from 'react-redux'
import CertificateItem from './CertificateItem'
import LanguageSelector from './LanguageSelector'
import { Col, Row } from 'react-styled-flexboxgrid'
import { GET_CERTIFICATE_ACHIEVEMENT } from '@queries/contentStack'

const CertificatePanelContent: React.FC<{ certs: ICertData[] }> = ({ certs }) => {
  const { lang, storedMessages } = useSelector(({ userProfile, intl: int }: StoreProps) => ({
    lang:
      userProfile?.info?.Type === PROFILE_TYPE.STUDENT
        ? userProfile?.info?.StudentProfile?.StudentProfileInfo?.PortalLanguage
        : userProfile?.info?.InstructorProfile?.instructorInfo?.PortalLanguage,
    storedMessages: int.messages,
  }))
  const [temp, setTemp] = useState<{ locale: string; value?: string }>()
  const [currentLang, setCurrentLang] = useState<{ [key: string]: any }>({})
  const [getTranslations, { data: translations, loading: transLoading }] = useLazyQuery<any>(GET_TRANSLATIONS)
  const [getContentStack, { loading: certLoading, data: certItems }] = useLazyQuery(
    GET_CERTIFICATE_ACHIEVEMENT(lang ?? 'en-us')
  )
  const intl = useIntl()

  const loading = transLoading || certLoading

  useEffect(() => {
    getContentStack()
  }, [])

  useEffect(() => {
    if (temp && translations) {
      setCurrentLang({ ...temp, messages: JSON.parse(translations?.localization?.translations || null) })
    }
  }, [JSON.stringify(translations)])

  // const isStarCertificate = (c?: any) => PROGRAM_TYPES.F2F_STAR.includes(c?.registration?.ProgramType ?? '')

  return (
    <CertPanel>
      {certs.map((cert, index) => {
        return (
          <div key={cert.id}>
            {index > 0 && <Spacer size="md" />}
            <Row>
              <Col xs={12} lg={5}>
                <CertShareLabel>
                  {intl.formatMessage({
                    id: 'Shareable certificate link:',
                    defaultMessage: 'Shareable certificate link:',
                  })}
                </CertShareLabel>
                <CertShareLink href={cert.link} target="_blank">
                  {cert.linkLabel}
                </CertShareLink>
                {!index && (
                  <>
                    <Spacer size="sm" />
                    <Spacer size="xxxs" />
                  </>
                )}
              </Col>
              <Col xs={12} lg={4}>
                {cert.type === CertificateTypes.ACHIEVEMENT}
                <>
                  <CertShareLink href={certItems?.contentstack?.items[0]?.url} target="_blank">
                    Common European Reference Framework (CEFR)
                  </CertShareLink>
                  {!index && (
                    <>
                      <Spacer size="sm" />
                      <Spacer size="xxxs" />
                    </>
                  )}
                </>
              </Col>
              {!index && (
                <Col xs={12} lg={2} lgOffset={1}>
                  <LanguageSelector
                    disabled={loading}
                    defaultVal={currentLang?.value || lang}
                    onChange={(value) => {
                      const locale = getLocale(value)
                      getTranslations({
                        variables: {
                          locale,
                        },
                      })
                      setTemp({
                        locale,
                        value,
                      })
                    }}
                  />
                </Col>
              )}
            </Row>
            <Spacer size="sm" />
            <CertWrapper>
              <IntlProvider
                locale={currentLang?.locale || getLocale(lang)}
                messages={currentLang?.messages || storedMessages}
                onError={(err) => {
                  // @ts-ignore
                  if (err.code === 'MISSING_TRANSLATION') {
                    // @ts-ignore
                    console.warn('Missing translation', err.message)
                    return
                  }
                  throw err
                }}
              >
                <CertificateItem cert={cert} loading={loading} />
              </IntlProvider>
            </CertWrapper>
          </div>
        )
      })}
    </CertPanel>
  )
}

export default React.memo(CertificatePanelContent)
